import { Box, Typography } from "@mui/material";
import type { FC, RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../theme/tokens";
import { SystemSearchField } from "../UIToolkit/Inputs/SystemSearchField";

/** Long enough that a fast typist doesn't refilter 300 rows per keystroke, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 200;
/** Below this the field stops fitting a full name plus the clear button. */
const SEARCH_MIN_WIDTH_PX = 330;

type TeamSearchBarProps = {
	/** The committed query (from the URL). */
	value: string;
	onChange: (value: string) => void;
	/** Rows currently shown. */
	shownCount: number;
	/** The whole department roster. */
	totalCount: number;
	/** Lets the page put the caret here — the Name column's "Filter by" hands off to this field. */
	inputRef?: RefObject<HTMLInputElement | null>;
};

/**
 * Search over the roster, plus the record count.
 *
 * Searching is the most common thing anyone does on this page and it used to be the least prominent
 * control on it — a combo box below the fold of the filter rail, styled as a dropdown. It now sits
 * full-width above the table, matches name, email and phone, and **ignores every active filter**:
 * looking someone up by name has to find them whether or not they are active, agency, or on a
 * contract type the rail happens to exclude.
 *
 * The count lives here rather than in the page title because this is the control people are
 * watching when the number moves.
 */
export const TeamSearchBar: FC<TeamSearchBarProps> = ({ value, onChange, shownCount, totalCount, inputRef }) => {
	const { t } = useTranslation();
	// Local state so typing stays responsive; the committed value round-trips through the URL.
	const [draft, setDraft] = useState(value);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	// Re-sync when the query changes from outside the field — Back, a pasted link, or Clear all.
	useEffect(() => {
		setDraft(value);
	}, [value]);

	useEffect(() => {
		if (draft === value) return;
		const timer = window.setTimeout(() => onChangeRef.current(draft), SEARCH_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [draft, value]);

	const isSearching = value.trim().length > 0;

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
			<SystemSearchField
				value={draft}
				onChange={setDraft}
				placeholder={t("team.search.placeholder")}
				// Password managers read the placeholder — "name, email or phone" is three identity tokens.
				name="team-roster-search"
				// Escape in a search box clears the search — and stops that same Escape from also being read by
				// the page as "clear the row selection". With nothing to clear it bubbles, so Escape still works
				// on the selection from here.
				clearOnEscape
				width={SEARCH_MIN_WIDTH_PX}
				maxWidth="100%"
				inputRef={inputRef}
				dataTestId="team-search"
			/>
			<Typography
				sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.MUTED, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
				data-testid="team-record-count"
			>
				{shownCount === totalCount ? t("team.count.total", { total: totalCount }) : t("team.count.of_total", { shown: shownCount, total: totalCount })}
			</Typography>
			{isSearching && (
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, whiteSpace: "nowrap" }} data-testid="team-search-scope-note">
					{t("team.search.ignoring_filters")}
				</Typography>
			)}
		</Box>
	);
};
